import React, { useState, useEffect, useRef, useContext } from "react";

import classNames from "classnames";
import html2canvas from "html2canvas";
import { Plus, Edit, Delete, MenuVertical, Download } from "icons";
import { isEmpty } from "ramda";
import { CSVLink } from "react-csv";
import { Responsive } from "react-grid-layout";
import { useParams } from "react-router-dom";
import { SizeMe } from "react-sizeme";

import chartsApi from "apis/charts";
import dashboardsApi from "apis/dashboards";
import layoutsApi from "apis/layouts";
import {
  PageLoader,
  Typography,
  Button,
  Dropdown,
  SubHeader,
} from "clearsense-ui/components";
import { EmptyState } from "components/Common";
import ThemeContext from "contexts/ThemeContext";

import ChartPane from "./ChartPane";
import FilterModal from "./FilterModal";
import MaximizedChart from "./MaximizedChart";
import MetricModal from "./MetricModal";
import QueryModal from "./QueryModal";
import { renderChart } from "./renderChart";
import SortByModal from "./SortByModal";

import { generateSQLQuery, initializeLayout } from "../../../utils";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [tableDetails, setTableDetails] = useState([]);
  const [charts, setCharts] = useState([]);
  const [showPane, setShowPane] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteCharts, setDeleteCharts] = useState([]);
  const [layouts, setLayouts] = useState({});
  const [tables, setTables] = useState({});
  const [dashboardName, setDashboardName] = useState("");
  const [tablePageInfo, setTablePageInfo] = useState({});
  const [addChartError, setAddChartError] = useState("");
  const [viewQuery, setViewQuery] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSortByModal, setShowSortByModal] = useState(false);
  const [maximizedChart, setMaximizedChart] = useState("");
  const [editChart, setEditChart] = useState({});

  const formRef = useRef();
  const modalRef = useRef();
  const filterModalRef = useRef();
  const sortByModalRef = useRef();
  const { dashboardId } = useParams();

  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    setTablePageInfo({
      pageNumber: {
        ...Object.keys(tables).reduce(
          (all, chartId) => ({ ...all, [chartId]: 1 }),
          {}
        ),
      },
      pageSize: {
        ...Object.keys(tables).reduce(
          (all, chartId) => ({ ...all, [chartId]: 100 }),
          {}
        ),
      },
    });
  }, [tables]);

  const fetchLayout = async () => {
    try {
      const res = await layoutsApi.show(dashboardId);
      setLayouts(JSON.parse(res.data.layout.stringified_layout || "{}"));
    } catch (error) {
      logger.error(error);
    }
  };

  const fetchCharts = async () => {
    try {
      const res = await dashboardsApi.show(dashboardId);
      setDashboardName(res.data.dashboard.title);
      setCharts(res.data.charts);
      if (!isEditMode) {
        res.data.charts.map(async chart => {
          const res = await chartsApi.fetchTable(chart.sql_query);
          setTables(prev => ({
            ...prev,
            [chart.id]: res.data.result || res.data.errors,
          }));
        });
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLayout();
    fetchCharts();
  }, []);

  const handleSubmit = async values => {
    setAddChartError("");
    try {
      const chartSQLQuery = generateSQLQuery(values, true);
      const res = await chartsApi.fetchTable(chartSQLQuery);
      if (res?.data?.errors) setAddChartError(res.data.errors);
      else {
        const chartSQLQuery = generateSQLQuery(values);
        const payload = {
          title: values.title,
          chart_type: values.chart_type.value,
          table: values.table.value,
          namekey: `${values.namekey.map(key => `${key.value}`).join(", ")}`,
          valuekey: `${
            values.valuekey &&
            values.valuekey
              .map(key => {
                if (key.value.includes(" as ") || key.value.includes(" AS ")) {
                  return key.value.split(/ as /gi)?.[1]?.replace(/"|\\n/g, "");
                }

                return `${key.value.replace(/"|\\n/g, "")}`;
              })
              .join(", ")
          }`,
          sql_query: chartSQLQuery,
          dashboard_id: dashboardId,
          schema: JSON.stringify(values.schema),
          group_by: JSON.stringify(values.namekey),
          metrics: JSON.stringify(values.valuekey),
          filters: JSON.stringify(values.filters),
          sort_by: JSON.stringify(values.sortBy),
          sort_direction: values.sortDirection,
          limit: JSON.stringify(values.row_limit),
        };
        setLoading(true);
        if (Object.keys(editChart).length) {
          await chartsApi.update(editChart.id, payload);
        } else await chartsApi.create(payload);
        setShowPane(false);
        await fetchCharts();
      }
    } catch (err) {
      logger.log(err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await layoutsApi.update(dashboardId, JSON.stringify(layouts));
      await Promise.all(
        deleteCharts.map(async chartId => await chartsApi.destroy(chartId))
      );
      setDeleteCharts([]);
      await fetchLayout();
      fetchCharts();
      setIsEditMode(false);
    } catch (error) {
      logger.error(error);
    }
  };

  const handleDiscard = async () => {
    setLoading(true);
    setDeleteCharts([]);
    try {
      await fetchLayout();
    } catch (error) {
      logger.error(error);
    } finally {
      setIsEditMode(false);
      setLoading(false);
    }
  };

  const downLoadAsJPEG = async (componentRef, name) => {
    try {
      const canvas = await html2canvas(document.getElementById(componentRef), {
        backgroundColor: "#f8f9f9",
      });
      var link = document.createElement("a");
      document.body.appendChild(link);
      link.download = `${name}.jpeg`;
      link.href = canvas.toDataURL("image/jpeg");
      link.target = "_blank";
      link.click();
    } catch (error) {
      logger.error(error);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <div className="p-4">
        <SubHeader
          breadcrumbs={[
            { label: "Dashboards", link: "/consumption/reporting/dashboards" },
            {
              label: dashboardName,
              link: "/",
              dataCy: "dashboard-title",
            },
          ]}
          className="p-4"
          actionBlock={
            <div className="space-x-2">
              {!isEditMode ? (
                <>
                  <Button
                    label="Create Chart"
                    data-cy="add-chart-button"
                    icon={Plus}
                    onClick={() => setShowPane(true)}
                  />
                  {!isEmpty(charts) && (
                    <Button
                      label="Download"
                      style="secondary"
                      icon={Download}
                      data-cy="dashboard-download-button"
                      onClick={() => downLoadAsJPEG("dashBoard", dashboardName)}
                    />
                  )}
                  <Button
                    label="Edit Layout"
                    style="secondary"
                    data-cy="edit-chart-layout-button"
                    icon={Edit}
                    disabled={isEmpty(charts)}
                    onClick={() => setIsEditMode(true)}
                  />
                </>
              ) : (
                <>
                  <Button
                    label="Save"
                    onClick={handleSave}
                    position="right"
                    data-cy="chart-edit-save-button"
                  />
                  <Button
                    label="Discard"
                    style="secondary"
                    data-cy="chart-edit-discard-button"
                    onClick={handleDiscard}
                  />
                </>
              )}
            </div>
          }
        />
      </div>
      {charts.length ? (
        <div id="dashBoard">
          <SizeMe refreshMode="debounce">
            {({ size }) => (
              <Responsive
                isBounded
                isDraggable={isEditMode}
                isResizable={isEditMode}
                width={size.width || 0}
                layouts={layouts}
                resizeHandles={["sw", "se", "nw", "ne"]}
                breakpoints={{ md: 996 }}
                cols={{ md: 4 }}
                margin={[15, 15]}
                onLayoutChange={(_, layouts) => setLayouts(layouts)}
              >
                {charts
                  .filter(chart => !deleteCharts.includes(chart.id))
                  .map(chart => (
                    <div
                      data-cy="chart-card"
                      id={chart.id}
                      key={chart.id}
                      className="flex flex-col bg-white dark:bg-cs-ui-gray-800 rounded shadow p-4 justify-start space-y-1.5"
                      data-grid={initializeLayout(chart, layouts)}
                    >
                      <div
                        className={classNames({
                          "mb-3": chart.chart_type !== "tabular",
                        })}
                      >
                        <div className="flex justify-between">
                          <Typography
                            style="h4"
                            type="semibold"
                            data-cy="chart-title"
                          >
                            {chart.title}
                          </Typography>
                          {!isEditMode && (
                            <Dropdown
                              data-html2canvas-ignore="true"
                              buttonStyle="text"
                              buttonProps={{
                                icon: () => (
                                  <MenuVertical className="dark:text-shade-light" />
                                ),
                              }}
                              data-cy="chart-dropdown-button"
                            >
                              <li
                                data-cy="charts-edit-button"
                                onClick={() => {
                                  setEditChart(chart);
                                  setShowPane(true);
                                }}
                              >
                                Edit Chart
                              </li>
                              <li
                                onClick={() => setViewQuery(chart.sql_query)}
                                data-cy="view-query-text"
                              >
                                View Query
                              </li>
                              <li
                                data-cy="download-as-image-text"
                                onClick={() =>
                                  downLoadAsJPEG(chart.id, chart.title)
                                }
                              >
                                Download as image
                              </li>
                              <li>
                                <CSVLink
                                  data={tables?.[chart?.id] || ""}
                                  filename={`${chart.title}.csv`}
                                  target="_blank"
                                  data-cy="export-as-csv-text"
                                >
                                  Export as CSV
                                </CSVLink>
                              </li>
                              <li
                                onClick={() => setMaximizedChart(chart)}
                                data-cy="maximize-chart-text"
                              >
                                Maximize Chart
                              </li>
                            </Dropdown>
                          )}
                        </div>
                      </div>
                      {isEditMode && (
                        <Delete
                          data-cy="delete-chart-button"
                          size={20}
                          className="absolute top-2 right-2 m-2 cursor-pointer hover:text-red-500"
                          onClick={() =>
                            setDeleteCharts([...deleteCharts, chart.id])
                          }
                        />
                      )}
                      {!tables[chart.id] ? (
                        <PageLoader />
                      ) : (
                        <>
                          {typeof tables[chart.id] === "string" && (
                            <EmptyState
                              illustration={<div></div>}
                              mainText="No Data"
                            />
                          )}
                          {typeof tables[chart.id] === "object" && (
                            <>
                              {renderChart({
                                chartData: chart,
                                tables,
                                tablePageInfo,
                                setTablePageInfo,
                              })}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  ))}
              </Responsive>
            )}
          </SizeMe>
        </div>
      ) : (
        <EmptyState mainText="No Charts Found" />
      )}
      <ChartPane
        formRef={formRef}
        modalRef={modalRef}
        sortByModalRef={sortByModalRef}
        filterModalRef={filterModalRef}
        showPane={showPane}
        tableDetails={tableDetails}
        addChartError={addChartError}
        editChart={editChart}
        setShowPane={setShowPane}
        setShowModal={setShowModal}
        setShowSortByModal={setShowSortByModal}
        setShowFilterModal={setShowFilterModal}
        setTableDetails={setTableDetails}
        setEditChart={setEditChart}
        handleSubmit={handleSubmit}
      />
      <MetricModal
        modalRef={modalRef}
        formRef={formRef}
        showModal={showModal}
        tableDetails={tableDetails}
        theme={theme}
        setShowModal={setShowModal}
      />
      <QueryModal
        viewQuery={viewQuery}
        theme={theme}
        setViewQuery={setViewQuery}
      />
      <FilterModal
        filterModalRef={filterModalRef}
        formRef={formRef}
        showFilterModal={showFilterModal}
        tableDetails={tableDetails}
        theme={theme}
        setShowFilterModal={setShowFilterModal}
      />
      <SortByModal
        sortByModalRef={sortByModalRef}
        formRef={formRef}
        showSortByModal={showSortByModal}
        tableDetails={tableDetails}
        theme={theme}
        setShowSortByModal={setShowSortByModal}
      />
      {maximizedChart && (
        <MaximizedChart
          maximizedChart={maximizedChart}
          tables={tables}
          tablePageInfo={tablePageInfo}
          setTablePageInfo={setTablePageInfo}
          setMaximizedChart={setMaximizedChart}
          setViewQuery={setViewQuery}
          downLoadAsJPEG={downLoadAsJPEG}
        />
      )}
    </>
  );
};

export default Dashboard;
